import { useNavigate } from 'react-router-dom'
import { ChevronRight, Loader2, Sparkles } from 'lucide-react'
import { gql, useQuery } from 'urql'

const CURRENT_REVIEW_QUERY = gql`
  query CurrentSavingsReviewCard {
    currentSavingsReview {
      id
      status
      insights {
        title
        body
        annualSaving
        category
      }
    }
  }
`

type Insight = { title: string; body: string; annualSaving: number; category: string }

export function SavingsReviewCard() {
  const navigate = useNavigate()
  const [{ data, fetching }] = useQuery({
    query: CURRENT_REVIEW_QUERY,
    requestPolicy: 'cache-and-network',
  })

  const review = data?.currentSavingsReview
  const status: string | undefined = review?.status
  const insights: Insight[] = review?.insights ?? []
  const top = insights[0]

  // Highlighted-finding state — only once there's a real result worth leading with.
  if (!fetching && status === 'READY' && top) {
    return (
      <button
        onClick={() => navigate('/savings-review')}
        className="w-full rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5 text-left hover:from-amber-100 hover:to-orange-100 transition-colors"
      >
        <div className="flex items-center gap-1.5 mb-3">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-xs font-semibold tracking-wide text-amber-800">SAVINGS REVIEW</span>
        </div>
        <div className="mb-1">
          <span className="text-3xl font-bold text-amber-600">${Math.round(top.annualSaving).toLocaleString()}</span>
          <span className="text-sm font-medium text-amber-400">/yr</span>
        </div>
        <p className="text-sm font-semibold text-gray-900 mb-0.5 truncate">{top.title}</p>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{top.body}</p>
        {insights.length > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-200">
            <span className="text-xs font-medium text-amber-800">
              +{insights.length - 1} more finding{insights.length - 1 === 1 ? '' : 's'}
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-amber-400" />
          </div>
        )}
      </button>
    )
  }

  // Compact banner — no review yet, still computing, ready with nothing to flag, or failed.
  let subtitle = 'Find savings in your receipts'
  if (status === 'PENDING') subtitle = 'Analyzing your spending…'
  else if (status === 'READY') subtitle = "You're all caught up — nothing to flag right now"
  else if (status === 'FAILED') subtitle = "Couldn't finish your last review — tap to retry"

  return (
    <button
      onClick={() => navigate('/savings-review')}
      className="w-full flex items-center justify-between rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 hover:bg-amber-100/70 transition-colors text-left"
    >
      <div className="flex items-center gap-2.5">
        {status === 'PENDING' ? (
          <Loader2 className="h-4 w-4 text-amber-500 shrink-0 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900 leading-none mb-0.5">Savings Review</p>
          <p className="text-xs text-amber-700/70">{subtitle}</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-amber-400 shrink-0" />
    </button>
  )
}
